/**
 * 所蔵館チップの配色・ラベル生成。
 *
 * OPAC 詳細から取れた予約数・貸出可否を色とバッジで表現するための共通ロジック。
 * BookCard (展開時の所蔵館一覧) と ReserveLibrarySheet (予約先の選択) で同じ見た目を使う。
 */

import { type Availability, type DerivedStats } from './library-stats'

/** 表示用に館情報と統計を結合した 1 館ぶんのエントリ */
export type RankedEntry = {
  libraryId: number
  name: string
  url: string | null
  isFetching: boolean
  derived: DerivedStats | null
}

/**
 * OPAC URL の安全性チェック。
 * Calil API 由来の URL でも javascript: 等の危険なスキームを開かないようにする。
 */
export const isSafeHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * availability に応じたチップ枚・バッジの色クラスを返す。
 *
 * - available (貸出可): emerald
 * - soon (全て貸出中だが予約 0): sky (返却後すぐに借りられる)
 * - waiting-light (turns 1〜2): amber
 * - waiting-heavy (turns >= 3): red
 * - unknown: ニュートラル
 */
export type ChipTone = 'available' | 'soon' | 'waiting-light' | 'waiting-heavy' | 'unknown'

export const pickChipTone = (availability: Availability, derived: DerivedStats | null): ChipTone => {
  if (availability === 'available') return 'available'
  if (availability === 'soon') return 'soon'
  if (availability === 'waiting') {
    // turns 未算出 (holdings 不明) の場合は情報不足なので保守的に light 側 (amber) に寄せる。
    // 予約はあるが実巡回数が読めない状態を「重待ち」(赤) と誤警告しないため。
    const turns = derived?.turns
    if (turns == null) return 'waiting-light'
    return turns >= 3 ? 'waiting-heavy' : 'waiting-light'
  }
  return 'unknown'
}

export const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  available: 'border-emerald-500/40 bg-emerald-500/10',
  soon: 'border-sky-500/40 bg-sky-500/10',
  'waiting-light': 'border-amber-500/40 bg-amber-500/10',
  'waiting-heavy': 'border-red-500/40 bg-red-500/10',
  unknown: 'border-border/60 bg-muted/20',
}

export const BADGE_TONE_CLASS: Record<ChipTone, string> = {
  available: 'bg-emerald-500/20 text-emerald-300',
  soon: 'bg-sky-500/20 text-sky-300',
  'waiting-light': 'bg-amber-500/20 text-amber-300',
  'waiting-heavy': 'bg-red-500/20 text-red-300',
  unknown: 'bg-muted/40 text-muted-foreground',
}

/**
 * StatsBadge のラベル文字列を availability ・数値から組み立てる。
 *
 * - available     : 「貸出可 N/M」(N=貸出可冊, M=所蔵)
 * - soon          : 「全部貸出中 / 予約なし」
 * - waiting-*     : 「予約 X / M冊 (Y巡待ち)」
 * - unknown       : 自前の parts をそのままフォールバック (取れている情報だけ)
 */
export const buildBadgeLabel = (availability: Availability, derived: DerivedStats): string => {
  const { reservations, holdings, loanable, turns } = derived
  if (availability === 'available') {
    if (holdings != null && loanable != null) return `貸出可 ${loanable}/${holdings}`
    if (loanable != null) return `貸出可 ${loanable}`
  }
  if (availability === 'soon') {
    return holdings != null ? `全て貸出中 (${holdings}冊) / 予約なし` : '全て貸出中 / 予約なし'
  }
  if (availability === 'waiting') {
    const parts: string[] = []
    if (reservations != null) parts.push(`予約 ${reservations}`)
    if (holdings != null) parts.push(`${holdings}冊`)
    const suffix = turns != null && turns > 0 ? ` (${turns}巡待ち)` : ''
    return `${parts.join(' / ')}${suffix}`
  }
  // unknown: 取れている値だけのフォールバック
  const parts: string[] = []
  if (reservations != null) parts.push(`予約 ${reservations}`)
  if (holdings != null) parts.push(`${holdings}冊`)
  return parts.join(' / ')
}

/**
 * 所蔵館の hover title を統計情報付きで組み立てる。
 */
export const buildChipTitle = (base: string, derived: DerivedStats | null): string => {
  if (!derived) return base
  const parts: string[] = []
  if (derived.holdings != null) parts.push(`所蔵 ${derived.holdings}冊`)
  if (derived.loanable != null) parts.push(`貸出可 ${derived.loanable}`)
  if (derived.on_loan != null) parts.push(`貸出中 ${derived.on_loan}`)
  if (derived.reservations != null) parts.push(`予約 ${derived.reservations}`)
  if (derived.turns != null && derived.turns > 0) parts.push(`${derived.turns}巡待ち`)
  if (parts.length === 0) return base
  return `${base} — ${parts.join(' / ')}`
}
